import Component from "../../lib/component";
import Tracker from "../../lib/tracker";
import {getTrackerOptions} from "../../lib/tracker_helpers";
import {ANY_SLIVER_THRESHOLDS, ExposureObserver, ExposureRules} from "../../helpers/exposure_observer";

export const AnalyticsWatcherConfig = {
    componentTypeKey: 'AnalyticsWatcher',
    baseHtmlClass: 'js--analytics_watcher',
    selector: '.js--analytics_watcher',

    initedHtmlClassModifier: 'inited',
    viewedHtmlClassModifier: 'viewed',

    datasetConfigKey: 'analytics_watcher__config',
    trackerOptionsDatasetConfigKey: 'tracker_options',
    analyticsEventNameDatasetConfigKey: 'event_name',
    analyticsParametersDatasetConfigKey: 'analytics_parameters',
    intersectionThresholdDatasetConfigKey: 'intersection_threshold',

    defaultIntersectionThreshold: ANY_SLIVER_THRESHOLDS,
};

/**
 * The AnalyticsWatcher component watches elements for when they are "viewed" and sends an event to a Tracker. The Tracker will handle the event appropriately for the environment (ie. native app, web).
 */
export class AnalyticsWatcher extends Component {

    constructor(id_or_element) {
        super({
            element: id_or_element,
            config: AnalyticsWatcherConfig
        });

        this.setDatasetConfig(AnalyticsWatcherConfig.datasetConfigKey);
        this.eventName = this.getAnalyticsEventName();
        this.analyticsParameters = this.getAnalyticsParameters();
        this.intersectionThreshold = this.getIntersectionThreshold();
        this.viewed = false;

        this.trackerOptions = getTrackerOptions(this.getDatasetConfigValue(AnalyticsWatcherConfig.trackerOptionsDatasetConfigKey));

        this.tracker = new Tracker(window, document, this.trackerOptions);

        // Set up observer to handle capturing stats impressions (views) of the component within it's parent element.
        this.viewedObserver = null;
        this.initViewedObserver();
        this.observeViewed();

        this.render();
        this.element.classList.add(this.htmlClassNameForState(AnalyticsWatcherConfig.initedHtmlClassModifier));
    }

    render() {
        this.conditionallySetHtmlClassState(this.isViewed(), AnalyticsWatcherConfig.viewedHtmlClassModifier);
    }

    /**
     * Set up the observer that counts one impression per time the element comes into view within its
     * scrollable parent element: any real sliver counts, and it counts again only after fully leaving.
     * Exposure is judged from geometry, and zero-area entries are refused.
     * See app/javascript/rankers/helpers/exposure_observer.js.
     * @return {undefined}
     */
    initViewedObserver() {
        if (typeof IntersectionObserver === 'undefined') return;
        this.viewedObserver = new ExposureObserver(this.element, {
            root: this.getParentScrollElement(),
            rootMargin: '10px',
            threshold: this.intersectionThreshold,
            rule: ExposureRules.anySliver,
            onExposure: () => {
                this.trackViewEvent();
                this.render();
            },
            // Fail CLOSED with no ResizeObserver: skip rather than observe, because a false
            // impression is worse than a missing one (see .claude/rules/native-webview-unmeasured-load.md).
            onNoResizeObserver: 'skip',
        });
        this.onDestroy(() => this.viewedObserver.destroy());
    }

    /**
     * Returns the component's parent element that is used for detecting if the component is visible.
     *
     * @return {Node|undefined} The DOM element that the component's element scrolls within.
     */
    getParentScrollElement() {
        return undefined; // This will mean the window is used as the scroll element.
    }

    /**
     * Tell the viewed intersection observer to start observing.
     * @return {undefined}
     */
    observeViewed() {
        if (!this.viewedObserver) return;
        // The helper defers the observe until the element actually has area, so the ~1.7s the native
        // apps hold the webview at size=0x0 cannot fire a phantom impression (a zero-area box reports
        // isIntersecting with ratio 1), and it refuses a zero-area entry after that too.
        this.viewedObserver.start();
    }

    /**
     * Tell the viewed intersection observer to stop observing.
     * @return {undefined}
     */
    unobserveViewed() {
        if (!this.viewedObserver) return;
        this.viewedObserver.stop();
    }

    trackViewEvent() {
        this.viewed = true;
        if (!this.eventName) console.warn(`[AnalyticsWatcher] trackViewEvent() eventName is blank!`, this.eventName);
        if (!this.tracker || !this.tracker.eventTracker) return;
        this.tracker.eventTracker(this.eventName, this.analyticsParameters);
    }

    isViewed() {
        return !!this.viewed;
    }

    getAnalyticsParameters() {
        const parameters = this.getDatasetConfigValue(AnalyticsWatcherConfig.analyticsParametersDatasetConfigKey);
        if (!parameters) return;
        return parameters;
    }

    getAnalyticsEventName() {
        const eventName = this.getDatasetConfigValue(AnalyticsWatcherConfig.analyticsEventNameDatasetConfigKey);
        if (!eventName) return;
        return eventName;
    }

    getIntersectionThreshold() {
        const threshold = this.getDatasetConfigValue(AnalyticsWatcherConfig.intersectionThresholdDatasetConfigKey);
        if (!threshold) return AnalyticsWatcherConfig.defaultIntersectionThreshold;
        return threshold;
    }

}
